import { useFormik } from "formik";
import React, { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { FiCamera, FiChevronDown } from "react-icons/fi";
import * as Yup from "yup";
import { useSubmitKycMutation } from "../../../features/account/kycSlice";
import Webcam from "react-webcam";

export enum KycDocumentType {
  ID = "id",
  PASSPORT = "passport",
  DRIVERS_LICENSE = "drivers_license",
  NIN = "nin",
  NIMC = "nimc",
}

interface UserInformationProps {
  setCurrentStep: React.Dispatch<
    React.SetStateAction<"country" | "init" | "nigerian" | "Success" | "Verified">
  >;
  country?: string;
}

export const UserInformation = ({
  setCurrentStep,
  country = "",
}: UserInformationProps) => {
  const [submitKyc, { isLoading, isSuccess }] = useSubmitKycMutation();

  const [step, setStep] = useState("1");

  const [images, setImages] = useState<{ front: any; back: any; selfie: any }>({
    front: null,
    back: null,
    selfie: null,
  });
  const [imageFiles, setImageFiles] = useState<{ front: any; back: any; selfie: any }>({
    front: null,
    back: null,
    selfie: null,
  });
  const [showCamera, setShowCamera] = useState(false);

  // Ref for front, back, and selfie image input
  const frontImageRef = React.useRef<HTMLInputElement>(null);
  const backImageRef = React.useRef<HTMLInputElement>(null);
  const selfieImageRef = React.useRef<HTMLInputElement>(null);
  const webcamRef = React.useRef<Webcam>(null);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      address: "",
      city: "",
      country: country,
      postalCode: "",
      dateOfBirth: "",
      placeOfBirth: "",
      documentType: "id",
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      address: Yup.string().required("Address is required"),
      city: Yup.string().required("City is required"),
      country: Yup.string().required("Country is required"),
      postalCode: Yup.string().required("Postal code is required"),
      dateOfBirth: Yup.string().required("Date of birth is required"),
      placeOfBirth: Yup.string().required("Place of birth is required"),
      documentType: Yup.string().required("Document type is required"),
    }),
    onSubmit: async (values) => {
      try {
        toast.loading("Uploading KYC...");

        const formData = new FormData();
        formData.append("firstName", values.firstName);
        formData.append("lastName", values.lastName);
        formData.append("address", values.address);
        formData.append("city", values.city);
        formData.append("country", values.country);
        formData.append("postalCode", values.postalCode);
        formData.append("dateOfBirth", values.dateOfBirth);
        formData.append("placeOfBirth", values.placeOfBirth);
        formData.append("documentType", values.documentType);

        if (imageFiles.front) formData.append("attachments", imageFiles.front);
        if (imageFiles.back) formData.append("attachments", imageFiles.back);
        if (imageFiles.selfie) formData.append("imageSelfie", imageFiles.selfie);

        await submitKyc(formData).unwrap();
        formik.resetForm();
        toast.dismiss();
        setCurrentStep("Success");
      } catch (error: any) {
        toast.dismiss();
        toast.error(error?.data?.message);
        console.log(error);
      }
    },
  });

  const handleNext = async () => {
    formik.setTouched({
      firstName: true,
      lastName: true,
      address: true,
      city: true,
      country: true,
      postalCode: true,
      dateOfBirth: true,
      placeOfBirth: true,
      documentType: true,
    });
    const validated = await formik.validateForm();
    Object.keys(validated).length === 0 && setStep("2");
  };

  const handleNextToSelfie = async () => {
    if (images.front && images.back) {
      setStep("3");
    } else {
      toast.error("Please upload both front and back image");
    }
  };

  const handleNextToReview = async () => {
    if (images.selfie) {
      setStep("4");
    } else {
      toast.error("Please capture your selfie");
    }
  };

  const startCamera = () => {
    setShowCamera(true);
  };

  const stopCamera = () => {
    setShowCamera(false);
  };

  const captureSelfie = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        // Convert data URL to blob
        fetch(imageSrc)
          .then(res => res.blob())
          .then(blob => {
            const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
            setImageFiles({ ...imageFiles, selfie: file });
            setImages({ ...images, selfie: imageSrc });
            stopCamera();
            toast.success('Selfie captured successfully!');
          })
          .catch(error => {
            console.error('Error processing image:', error);
            toast.error('Failed to capture selfie');
          });
      } else {
        toast.error('Camera not ready. Please try again.');
      }
    } else {
      toast.error('Camera not ready. Please try again.');
    }
  }, [imageFiles, images]);

  // Stop camera when leaving selfie step
  useEffect(() => {
    if (step !== "3" && showCamera) {
      stopCamera();
    }
  }, [step]);


  function _renderStepContent(step: string) {
    switch (step) {
      case "1":
        return (
          <div>
            <div className="flex flex-col gap-2">
              <label htmlFor="bankCode" className="text-xs text-white/70">
                Select Document Type
              </label>
              <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
                <select
                  name="documentType"
                  id="documentType"
                  value={formik.values.documentType}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "documentType",
                      e.target.selectedOptions[0].value
                    );
                    formik.handleChange(e);
                  }}
                  className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full capitalize"
                >
                  {Object.values(KycDocumentType).map((type) => (
                    <option key={type} value={type}>{type.replace("_", " ")}</option>
                  ))}
                </select>
                <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10" />
              </div>
              <p className="text-red-500 text-xs">
                {formik.touched.documentType &&
                  Boolean(formik.errors.documentType) &&
                  formik.errors.documentType}
              </p>
            </div>

            <div className="flex gap-2">
              <div className="flex flex-col gap-2">
                <label htmlFor="firstName" className="text-xs text-white/70">
                  First Name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  placeholder="John"
                  className="form-input"
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.firstName &&
                    Boolean(formik.errors.firstName) &&
                    formik.errors.firstName}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="lastName" className="text-xs text-white/70">
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  placeholder="Doe"
                  className="form-input"
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.lastName &&
                    Boolean(formik.errors.lastName) &&
                    formik.errors.lastName}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="address" className="text-xs text-white/70">
                Address
              </label>
              <input
                id="address"
                name="address"
                type="text"
                value={formik.values.address}
                onChange={formik.handleChange}
                placeholder="123 Main St"
                className="form-input"
              />

              <p className="text-red-500 text-xs">
                {formik.touched.address &&
                  Boolean(formik.errors.address) &&
                  formik.errors.address}
              </p>
            </div>

            <div className="flex gap-2">
              <div className="flex flex-col gap-2">
                <label htmlFor="city" className="text-xs text-white/70">
                  City
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  placeholder="City"
                  className="form-input"
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.city &&
                    Boolean(formik.errors.city) &&
                    formik.errors.city}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="country" className="text-xs text-white/70">
                  Country
                </label>
                <input
                  id="country"
                  name="country"
                  type="text"
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  placeholder="Country"
                  className={`form-input ${country ? 'bg-gray-700 cursor-not-allowed' : ''}`}
                  disabled={!!country}
                  readOnly={!!country}
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.country &&
                    Boolean(formik.errors.country) &&
                    formik.errors.country}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="postalCode" className="text-xs text-white/70">
                  Post Code
                </label>
                <input
                  id="postalCode"
                  name="postalCode"
                  type="text"
                  value={formik.values.postalCode}
                  onChange={formik.handleChange}
                  placeholder="1234"
                  className="form-input"
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.postalCode &&
                    Boolean(formik.errors.postalCode) &&
                    formik.errors.postalCode}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <div className="flex flex-col gap-2">
                <label htmlFor="dateOfBirth" className="text-xs text-white/70">
                  Date of Birth
                </label>
                <input
                  id="dateOfBirth"
                  name="dateOfBirth"
                  type="date"
                  value={formik.values.dateOfBirth}
                  onChange={formik.handleChange}
                  placeholder="Date of birth"
                  className="form-input"
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.dateOfBirth &&
                    Boolean(formik.errors.dateOfBirth) &&
                    formik.errors.dateOfBirth}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="placeOfBirth" className="text-xs text-white/70">
                  Place of Birth
                </label>
                <input
                  id="placeOfBirth"
                  name="placeOfBirth"
                  type="text"
                  value={formik.values.placeOfBirth}
                  onChange={formik.handleChange}
                  placeholder="Place of birth"
                  className="form-input"
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.placeOfBirth &&
                    Boolean(formik.errors.placeOfBirth) &&
                    formik.errors.placeOfBirth}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="btn-primary py-3 rounded-md w-full mt-12"
              onClick={handleNext}
            >
              Next
            </button>
          </div>
        );

      case "2":
        return (
          <div>
            {/* Upload and preview both front and back image */}
            <div className="hidden">
              <input
                id="frontImage"
                name="frontImage"
                type="file"
                ref={frontImageRef}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setImageFiles({ ...imageFiles, front: file });
                    setImages({ ...images, front: URL.createObjectURL(file) });
                  }
                }}
              />
              <input
                id="backImage"
                name="backImage"
                type="file"
                ref={backImageRef}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setImageFiles({ ...imageFiles, back: file });
                    setImages({ ...images, back: URL.createObjectURL(file) });
                  }
                }}
              />

              {/* Display Images */}
            </div>
            <div className="flex space-x-4">
              <div className="flex flex-col gap-2 w-1/2">
                <label htmlFor="firstName" className="text-xs text-white/70">
                  Front Image
                </label>
                {images.front ? (
                  <div className="h-40 relative">
                    <div
                      className="absolute top-0 left-0 w-full h-full bg-black/60 flex justify-center items-center cursor-pointer"
                      onClick={() => {
                        frontImageRef.current?.click();
                      }}
                    >
                      <FiCamera className="text-white/60 text-4xl" />
                    </div>
                    <img
                      src={images.front}
                      alt="front"
                      className="h-full w-full object-scale-down"
                    />
                  </div>
                ) : (
                  <div
                    className="h-40 bg-gray-600 flex justify-center items-center flex-col cursor-pointer"
                    onClick={() => {
                      frontImageRef.current?.click();
                    }}
                  >
                    <FiCamera className="text-white/60 text-4xl" />
                    <p className="text-xs text-center text-gray-200 mt-2">
                      Drop or click to upload back image
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 w-1/2">
                <label htmlFor="lastName" className="text-xs text-white/70">
                  Back Image
                </label>
                {images.back ? (
                  <div className="h-40 relative justify-center items-center">
                    <div
                      className="absolute top-0 left-0 w-full h-full bg-black/60 flex justify-center items-center cursor-pointer"
                      onClick={() => {
                        backImageRef.current?.click();
                      }}
                    >
                      <FiCamera className="text-white/60 text-4xl" />
                    </div>
                    <img
                      src={images.back}
                      alt="back"
                      className="h-full w-full object-scale-down"
                    />
                  </div>
                ) : (
                  <div
                    className="h-40 bg-gray-600 flex justify-center items-center flex-col cursor-pointer"
                    onClick={() => {
                      backImageRef.current?.click();
                    }}
                  >
                    <FiCamera className="text-white/60 text-4xl" />
                    <p className="text-xs text-center text-gray-200 mt-2">
                      Drop or click to upload back image
                    </p>
                  </div>
                )}
              </div>
            </div>
            {/* Next Button */}
            <div className="flex justify-between items-center mt-8">
              <button
                type="button"
                className="btn-secondary py-3 rounded-md w-16"
                onClick={() => setStep("1")}
              >
                Back
              </button>
              <button
                type="button"
                className="btn-primary py-3 rounded-md w-16"
                onClick={handleNextToSelfie}
              >
                Next
              </button>
            </div>
          </div>
        );

      case "3":
        // Selfie capture step
        return (
          <div>
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-white mb-2">Capture Your Selfie</h3>
              <p className="text-sm text-gray-400">
                Please take a clear selfie for identity verification
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {showCamera ? (
                <div className="flex flex-col gap-4">
                  <div className="relative mx-auto w-80 h-64 bg-black rounded-lg overflow-hidden">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      width={320}
                      height={256}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        width: 320,
                        height: 256,
                        facingMode: "user"
                      }}
                      className="w-full h-full object-cover"
                      style={{ transform: 'scaleX(-1)' }} // Mirror the video for selfie
                    />
                    <div className="absolute inset-0 border-4 border-white/30 rounded-lg pointer-events-none">
                      <div className="absolute top-4 left-4 right-4 h-8 bg-black/50 rounded flex items-center justify-center">
                        <p className="text-white text-sm">Position your face in the center</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4 justify-center">
                    <button
                      type="button"
                      className="btn-secondary py-2 px-4 rounded-md"
                      onClick={stopCamera}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary py-2 px-4 rounded-md flex items-center gap-2"
                      onClick={captureSelfie}
                    >
                      <FiCamera className="w-4 h-4" />
                      Capture
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-white/70 text-center">
                    Selfie Image
                  </label>
                  {images.selfie ? (
                    <div className="h-64 relative mx-auto w-64">
                      <div
                        className="absolute top-0 left-0 w-full h-full bg-black/60 flex justify-center items-center cursor-pointer rounded-lg"
                        onClick={startCamera}
                      >
                        <FiCamera className="text-white/60 text-4xl" />
                      </div>
                      <img
                        src={images.selfie}
                        alt="selfie"
                        className="h-full w-full object-cover rounded-lg"
                      />
                    </div>
                  ) : (
                    <div
                      className="h-64 bg-gray-600 flex justify-center items-center flex-col cursor-pointer mx-auto w-64 rounded-lg"
                      onClick={startCamera}
                    >
                      <FiCamera className="text-white/60 text-4xl" />
                      <p className="text-xs text-center text-gray-200 mt-2">
                        Tap to open camera
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4">
                <div className="flex items-start">
                  <FiCamera className="w-5 h-5 text-blue-400 mt-0.5 mr-2" />
                  <div>
                    <h4 className="font-semibold text-blue-300 text-sm">Selfie Guidelines</h4>
                    <ul className="text-xs text-blue-200 mt-1 space-y-1">
                      <li>• Look directly at the camera</li>
                      <li>• Ensure good lighting</li>
                      <li>• Remove glasses if possible</li>
                      <li>• Keep your face centered in the frame</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-8">
              <button
                type="button"
                className="btn-secondary py-3 rounded-md w-16"
                onClick={() => setStep("2")}
              >
                Back
              </button>
              <button
                type="button"
                className="btn-primary py-3 rounded-md w-16"
                onClick={handleNextToReview}
              >
                Next
              </button>
            </div>
          </div>
        );

      case "4":
        // Display all the filled information
        return (
          <div>
            <div className="flex flex-col gap-2">
              <label htmlFor="firstName" className="text-xs text-white/70">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                placeholder="John"
                className="form-input"
                disabled
              />

              <p className="text-red-500 text-xs">
                {formik.touched.firstName &&
                  Boolean(formik.errors.firstName) &&
                  formik.errors.firstName}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="lastName" className="text-xs text-white/70">
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                placeholder="Doe"
                className="form-input"
                disabled
              />

              <p className="text-red-500 text-xs">
                {formik.touched.lastName &&
                  Boolean(formik.errors.lastName) &&
                  formik.errors.lastName}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="address" className="text-xs text-white/70">
                Address
              </label>
              <input
                id="address"
                name="address"
                type="text"
                value={formik.values.address}
                onChange={formik.handleChange}
                placeholder="123 Main St"
                className="form-input"
                disabled
              />

              <p className="text-red-500 text-xs">
                {formik.touched.address &&
                  Boolean(formik.errors.address) &&
                  formik.errors.address}
              </p>
            </div>

            <div className="flex gap-2">
              <div className="flex flex-col gap-2">
                <label htmlFor="city" className="text-xs text-white/70">
                  City
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  placeholder="City"
                  className="form-input"
                  disabled
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.city &&
                    Boolean(formik.errors.city) &&
                    formik.errors.city}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="country" className="text-xs text-white/70">
                  Country
                </label>
                <input
                  id="country"
                  name="country"
                  type="text"
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  placeholder="Country"
                  className="form-input"
                  disabled
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.country &&
                    Boolean(formik.errors.country) &&
                    formik.errors.country}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="postalCode" className="text-xs text-white/70">
                  Post Code
                </label>
                <input
                  id="postalCode"
                  name="postalCode"
                  type="text"
                  value={formik.values.postalCode}
                  onChange={formik.handleChange}
                  placeholder="1234"
                  className="form-input"
                  disabled
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.postalCode &&
                    Boolean(formik.errors.postalCode) &&
                    formik.errors.postalCode}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <div className="flex flex-col gap-2">
                <label htmlFor="dateOfBirth" className="text-xs text-white/70">
                  Date of Birth
                </label>
                <input
                  id="dateOfBirth"
                  name="dateOfBirth"
                  type="date"
                  value={formik.values.dateOfBirth}
                  onChange={formik.handleChange}
                  placeholder="Date of birth"
                  className="form-input"
                  disabled
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.dateOfBirth &&
                    Boolean(formik.errors.dateOfBirth) &&
                    formik.errors.dateOfBirth}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="placeOfBirth" className="text-xs text-white/70">
                  Place of Birth
                </label>
                <input
                  id="placeOfBirth"
                  name="placeOfBirth"
                  type="text"
                  value={formik.values.placeOfBirth}
                  onChange={formik.handleChange}
                  placeholder="Place of birth"
                  className="form-input"
                  disabled
                />

                <p className="text-red-500 text-xs">
                  {formik.touched.placeOfBirth &&
                    Boolean(formik.errors.placeOfBirth) &&
                    formik.errors.placeOfBirth}
                </p>
              </div>
            </div>

            {/* Display the front, back, and selfie images */}
            <div className="space-y-4">
              <div className="flex space-x-4">
                <div className="flex flex-col gap-2 w-1/2">
                  <label className="text-xs text-white/70">
                    Front Image
                  </label>
                  <div className="h-40">
                    <img
                      src={images.front}
                      alt="front"
                      className="h-full w-full object-scale-down"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2 w-1/2">
                  <label className="text-xs text-white/70">
                    Back Image
                  </label>
                  <div className="h-40">
                    <img
                      src={images.back}
                      alt="back"
                      className="h-full w-full object-scale-down"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="flex flex-col gap-2 w-64">
                  <label className="text-xs text-white/70 text-center">
                    Selfie Image
                  </label>
                  <div className="h-40">
                    <img
                      src={images.selfie}
                      alt="selfie"
                      className="h-full w-full object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-8">
              <button
                type="button"
                className="btn-secondary py-3 rounded-md w-16"
                onClick={() => setStep("3")}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn-primary py-3 rounded-md w-16"
              >
                Submit
              </button>
            </div>
          </div>
        );
    }
  }

  return (
    <form action="" className="mt-8 w-full" onSubmit={formik.handleSubmit}>
      {_renderStepContent(step)}
    </form>
  );
};
